import type { DriverEvent } from '@kmdx/contracts';
import { getDeviceId } from './ids';
import type { BootstrapDto } from './types';

/**
 * Единственото място, което говори с мрежата. Всичко останало пише в Dexie и
 * оставя опашката да се оправя — така нито един екран няма клон „ако няма връзка".
 */

const BASE: string = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api';

export class ApiError extends Error {
  readonly status: number;
  /** 4xx (без 408/429) е грешка в данните — повторният опит няма да помогне. */
  readonly permanent: boolean;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.permanent = status >= 400 && status < 500 && status !== 408 && status !== 429;
  }
}

export class OfflineError extends Error {
  constructor() {
    super('Няма връзка');
    this.name = 'OfflineError';
  }
}

/** ─── Токени ──────────────────────────────────────────────────────────── */

interface TokenPair {
  accessToken: string;
  expiresIn: number;
  refreshToken: string;
  refreshExpiresAt: string;
}

let accessToken: string | null = null;
let accessExpiresAt = 0;
let refreshToken: string | null = null;
let refreshInFlight: Promise<string> | null = null;

type RotationHandler = (token: string) => void | Promise<void>;
let onRotated: RotationHandler | null = null;

export function setRefreshToken(token: string | null): void {
  refreshToken = token;
  if (!token) {
    accessToken = null;
    accessExpiresAt = 0;
  }
}

/** Сървърът може да върне нов токен за подновяване — трябва да се презапише шифрован. */
export function onRefreshTokenRotated(handler: RotationHandler | null): void {
  onRotated = handler;
}

async function requestJson<T>(path: string, init: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, init);
  } catch {
    throw new OfflineError();
  }
  if (!response.ok) {
    throw new ApiError(response.status, await readError(response));
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

async function readError(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { message?: string | string[] };
    const message = Array.isArray(body.message) ? body.message.join('; ') : body.message;
    if (message) return message;
  } catch {
    // тялото не е JSON — остава общото съобщение
  }
  return `Сървърът отговори с грешка ${response.status}`;
}

async function refreshAccess(): Promise<string> {
  if (!refreshToken) throw new ApiError(401, 'Устройството не е свързано');
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    const pair = await requestJson<TokenPair>('/auth/driver/refresh', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refreshToken, deviceId: getDeviceId() }),
    });
    accessToken = pair.accessToken;
    // Минута резерв, за да не изтече токенът точно между проверката и заявката.
    accessExpiresAt = Date.now() + Math.max(0, pair.expiresIn - 60) * 1000;
    if (pair.refreshToken && pair.refreshToken !== refreshToken) {
      refreshToken = pair.refreshToken;
      await onRotated?.(pair.refreshToken);
    }
    return pair.accessToken;
  })();

  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

async function authHeader(): Promise<Record<string, string>> {
  if (!accessToken || Date.now() >= accessExpiresAt) await refreshAccess();
  return { authorization: `Bearer ${accessToken ?? ''}` };
}

async function authedJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = { ...(init.headers as Record<string, string> | undefined), ...(await authHeader()) };
  try {
    return await requestJson<T>(path, { ...init, headers });
  } catch (error) {
    // Един повторен опит след ново взимане на токен: сесията може да е отнета
    // и подновена, докато телефонът е бил без обхват.
    if (error instanceof ApiError && error.status === 401) {
      accessToken = null;
      const retryHeaders = {
        ...(init.headers as Record<string, string> | undefined),
        ...(await authHeader()),
      };
      return requestJson<T>(path, { ...init, headers: retryHeaders });
    }
    throw error;
  }
}

function jsonBody(payload: unknown): RequestInit {
  return {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  };
}

/** ─── Свързване на устройството ───────────────────────────────────────── */

export interface EnrolmentResult extends TokenPair {
  driver: { id: string; fullName: string; phone: string | null };
}

export async function redeemEnrolment(code: string): Promise<EnrolmentResult> {
  const result = await requestJson<EnrolmentResult>(
    '/auth/driver/enrolment/redeem',
    jsonBody({ code, deviceId: getDeviceId() }),
  );
  refreshToken = result.refreshToken;
  accessToken = result.accessToken;
  accessExpiresAt = Date.now() + Math.max(0, result.expiresIn - 60) * 1000;
  return result;
}

/** ─── Данни ───────────────────────────────────────────────────────────── */

export function fetchBootstrap(since?: string): Promise<BootstrapDto> {
  const query = since ? `?since=${encodeURIComponent(since)}` : '';
  return authedJson<BootstrapDto>(`/driver/bootstrap${query}`);
}

export interface SyncResult {
  accepted: string[];
  serverTime: string;
}

export function postEvents(events: DriverEvent[]): Promise<SyncResult> {
  return authedJson<SyncResult>('/driver/sync', jsonBody({ events }));
}

export interface DocumentInitResult {
  documentId: string;
  /** Ако сървърът вече има файл със същата контролна сума, качване не е нужно. */
  alreadyStored: boolean;
  upload: { url: string; method: string; headers: Record<string, string> } | null;
}

export function initDocument(payload: Record<string, unknown>): Promise<DocumentInitResult> {
  return authedJson<DocumentInitResult>('/driver/documents', jsonBody(payload));
}

export function completeDocument(clientDocId: string): Promise<void> {
  return authedJson<void>(`/driver/documents/${encodeURIComponent(clientDocId)}/complete`, {
    method: 'POST',
  });
}

export function postExpense(payload: Record<string, unknown>): Promise<void> {
  return authedJson<void>('/driver/expenses', jsonBody(payload));
}

export function postMessage(payload: Record<string, unknown>): Promise<void> {
  return authedJson<void>('/driver/messages', jsonBody(payload));
}

export function postVehicleCheck(payload: Record<string, unknown>): Promise<void> {
  return authedJson<void>('/driver/vehicle-checks', jsonBody(payload));
}

/**
 * Качване на самия файл. `fetch` няма събитие за напредък, а шофьорът трябва
 * да вижда, че снимката тръгва — затова тук е XHR, а не fetch.
 */
export function uploadBlob(
  target: { url: string; method: string; headers: Record<string, string> },
  blob: Blob,
  onProgress: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open(target.method || 'PUT', target.url, true);
    for (const [name, value] of Object.entries(target.headers ?? {})) {
      request.setRequestHeader(name, value);
    }
    request.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) onProgress(event.loaded / event.total);
    };
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        onProgress(1);
        resolve();
      } else {
        reject(new ApiError(request.status, `Качването върна грешка ${request.status}`));
      }
    };
    request.onerror = () => reject(new OfflineError());
    request.ontimeout = () => reject(new OfflineError());
    request.timeout = 120_000;
    request.send(blob);
  });
}
